import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { updateOrderStatus, deleteOrder } from '../services/db';
import { useFirebase } from '../hooks/useFirebase';
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaClipboardList, FaStickyNote } from 'react-icons/fa';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 8rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Title = styled.h2`
  color: #2d3436;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
`;

const LogoutButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #ff5252;
    transform: translateY(-1px);
  }
`;

const OrdersContainer = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 2px solid #ff6b6b;

  &:hover {
    transform: translateY(-2px);
  }
`;

const OrderHeader = styled.div`
  background: #9bc3e8;
  padding: 1.5rem;
  border-bottom: 1px solid #edf2f7;
`;

const OrderTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;

  h3 {
    color: #2d3436;
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
  }
`;

const OrderStatus = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    switch (props.status) {
      case 'approved':
        return '#e3fcec';
      case 'cancelled':
        return '#fee7e7';
      default:
        return '#fff7e6';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved':
        return '#0a8554';
      case 'cancelled':
        return '#dc2626';
      default:
        return '#d97706';
    }
  }};
`;

const OrderContent = styled.div`
  padding: 1.5rem;
  flex: 1;
`;

const InfoGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #4a5568;
  font-size: 0.95rem;

  svg {
    color: #718096;
    margin-right: 0.75rem;
    font-size: 1rem;
  }
`;

const ItemsList = styled.div`
  background: #9bc3e8;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
`;

const ItemsHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  color: #1a365d;
  font-weight: 600;
  
  svg {
    margin-right: 0.5rem;
  }
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }
`;

const TotalRow = styled(Item)`
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid #e2e8f0;
  font-weight: 600;
  color: #2d3436;
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
  padding: 1.5rem;
  background: #9bc3e8;
  border-top: 1px solid #edf2f7;
  margin-top: auto;
`;

const ActionButton = styled.button`
  background: ${props => props.approve ? '#4ade80' : props.cancel ? '#f87171' : props.delete ? '#ef4444' : '#93c6f5'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    opacity: ${props => props.disabled ? 0.5 : 0.9};
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 3rem;
  color: #718096;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #dc2626;
  background: #fee2e2;
  border-radius: 8px;
  margin: 2rem 0;
`;

const RetryButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #dc2626;
  }
`;

const NoOrders = styled.div`
  text-align: center;
  padding: 3rem;
  color: #718096;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  font-size: 1.1rem;
`;

const SpecialInstructions = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;

  .header {
    display: flex;
    align-items: center;
    color: #1a365d;
    font-weight: 600;
    margin-bottom: 0.5rem;

    svg {
      margin-right: 0.5rem;
      color: #4a5568;
    }
  }

  .content {
    color: #4a5568;
    font-size: 0.95rem;
    white-space: pre-wrap;
  }
`;

const AdminOrders = () => {
  const { isAuthenticated, logout } = useAuth();
  const { orders, loading, error } = useFirebase();
  const [deletingOrders, setDeletingOrders] = useState(new Set());

  const handleStatusUpdate = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const orderDetails = `
Order #${order.id.slice(0, 8)}
Customer: ${order.customerName}
Total: $${order.total.toFixed(2)}
Items: ${order.items.map(item => `\n- ${item.quantity}x ${item.name}`).join('')}
`;

    if (newStatus === 'cancelled') {
      if (!window.confirm(`Are you sure you want to cancel this order?\n\n${orderDetails}`)) {
        return;
      }
    }

    try {
      await updateOrderStatus(orderId, newStatus);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status. Please try again.');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const orderDetails = `
Order #${order.id.slice(0, 8)}
Status: ${order.status}
Customer: ${order.customerName}
Total: $${order.total.toFixed(2)}
Items: ${order.items.map(item => `\n- ${item.quantity}x ${item.name}`).join('')}
Created: ${new Date(order.createdAt).toLocaleString()}
`;

    if (!window.confirm(`Are you sure you want to permanently delete this order?\n\n${orderDetails}`)) {
      return;
    }

    setDeletingOrders(prev => new Set([...prev, orderId]));
    try {
      await deleteOrder(orderId);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Failed to delete order. Please try again.');
    } finally {
      setDeletingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" />;
  }

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading orders...</LoadingSpinner>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorMessage>
          Error loading orders: {error.message}
          <RetryButton onClick={() => window.location.reload()}>
            Retry
          </RetryButton>
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>Order Management</Title>
        <HeaderButtons>
          <LogoutButton onClick={logout}>Logout</LogoutButton>
        </HeaderButtons>
      </Header>

      <OrdersContainer>
        {orders.length === 0 ? (
          <NoOrders>No orders found.</NoOrders>
        ) : (
          orders.map(order => (
            <OrderCard key={order.id}>
              <OrderHeader>
                <OrderTitle>
                  <h3>Order #{order.id.slice(0, 8)}</h3>
                  <OrderStatus status={order.status}>{order.status}</OrderStatus>
                </OrderTitle>
              </OrderHeader>

              <OrderContent>
                <InfoGroup>
                  <InfoRow>
                    <FaUser />
                    <span>{order.customerName}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaEnvelope />
                    <span>{order.email}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaPhone />
                    <span>{order.phone}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaMapMarkerAlt />
                    <span>{order.address}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaClock />
                    <span>{new Date(order.createdAt).toLocaleString()}</span>
                  </InfoRow>
                </InfoGroup>

                <ItemsList>
                  <ItemsHeader>
                    <FaClipboardList />
                    <span>Order Items</span>
                  </ItemsHeader>
                  {order.items.map((item, index) => (
                    <Item key={index}>
                      <span>{item.quantity}x {item.name}</span>
                      <span>${(item.totalPrice).toFixed(2)}</span>
                    </Item>
                  ))}
                  <TotalRow>
                    <span>Total Amount</span>
                    <span>${order.total.toFixed(2)}</span>
                  </TotalRow>
                </ItemsList>

                <SpecialInstructions>
                  <div className="header">
                    <FaStickyNote />
                    <span>Special Instructions</span>
                  </div>
                  <div className="content">
                    {order.specialInstructions || 'No special instructions provided'}
                  </div>
                </SpecialInstructions>
              </OrderContent>

              <ButtonGroup>
                <ActionButton
                  approve
                  onClick={() => handleStatusUpdate(order.id, 'approved')}
                  disabled={order.status === 'approved' || order.status === 'cancelled'}
                >
                  Approve Order
                </ActionButton>
                <ActionButton
                  cancel
                  onClick={() => handleStatusUpdate(order.id, 'cancelled')}
                  disabled={order.status === 'approved' || order.status === 'cancelled'}
                >
                  Cancel Order
                </ActionButton>
                <ActionButton
                  delete
                  onClick={() => handleDeleteOrder(order.id)}
                  disabled={deletingOrders.has(order.id) || (order.status !== 'approved' && order.status !== 'cancelled')}
                >
                  {deletingOrders.has(order.id) ? 'Deleting...' : 
                   (order.status !== 'approved' && order.status !== 'cancelled') ? 'Cannot Delete Yet' : 
                   'Delete Order'}
                </ActionButton>
              </ButtonGroup>
            </OrderCard>
          ))
        )}
      </OrdersContainer>
    </PageContainer>
  );
};

export default AdminOrders;
